// Централизованный логгер для замены console.*
type LogLevel = "DEBUG" | "INFO" | "WARNING" | "ERROR" | "CRITICAL" | "LOG";

type LogContext = Record<string, unknown> | undefined;

interface LogEntry {
  level: LogLevel;
  message: string;
  context: Record<string, unknown>;
  timestamp: number;
  service?: string;
}

class Logger {
  private static isDevelopment = process.env.NODE_ENV === "development";
  private static logs: LogEntry[] = [];
  private static readonly maxLogs = 100;

  private static recordLog(
    level: LogLevel,
    message: string,
    context?: Record<string, unknown>,
    service?: string,
  ) {
    const entry: LogEntry = {
      level,
      message,
      context: context ?? {},
      timestamp: Date.now(),
      service,
    };
    Logger.logs.push(entry);
    if (Logger.logs.length > Logger.maxLogs) {
      Logger.logs.shift();
    }

    if (Logger.isDevelopment) {
      const prefix = service ? `[${service}]` : "";
      switch (level) {
        case "ERROR":
        case "CRITICAL":
          console.error(`[${level}]${prefix} ${message}`, context);
          break;
        case "WARNING":
          console.warn(`[${level}]${prefix} ${message}`, context);
          break;
        case "INFO":
        case "LOG":
          console.info(`[${level}]${prefix} ${message}`, context);
          break;
        default:
          // DEBUG: только в консоли разработчика и только на уровне debug,
          // чтобы отладочный поток можно было отключить фильтром браузера.
          console.debug(`[${level}]${prefix} ${message}`, context);
          break;
      }
    }
  }

  static log(message: string, context?: Record<string, unknown>) {
    this.recordLog("LOG", message, context);
  }

  static info(message: string, context?: Record<string, unknown>) {
    this.recordLog("INFO", message, context);
  }

  static warn(message: string, context?: Record<string, unknown>) {
    this.recordLog("WARNING", message, context);
  }

  static warning(message: string, context?: Record<string, unknown>) {
    this.recordLog("WARNING", message, context);
  }

  static debug(message: string, context?: Record<string, unknown>) {
    this.recordLog("DEBUG", message, context);
  }

  static error(
    message: string,
    error?: Error,
    context?: Record<string, unknown>,
  ) {
    this.recordLog("ERROR", message, {
      ...(context ?? {}),
      error_message: error?.message,
      error_stack: error?.stack,
    });
  }

  static critical(
    message: string,
    error?: Error,
    context?: Record<string, unknown>,
  ) {
    this.recordLog("CRITICAL", message, {
      ...(context ?? {}),
      error_message: error?.message,
      error_stack: error?.stack,
    });
  }

  static componentEvent(
    component: string,
    event: string,
    context?: Record<string, unknown>,
  ) {
    this.recordLog(
      "INFO",
      `[Component:${component}] ${event}`,
      context ?? {},
      "Component",
    );
  }

  static apiCall(
    endpoint: string,
    method: string,
    params?: unknown,
    context?: Record<string, unknown>,
  ) {
    this.recordLog(
      "INFO",
      `[API] ${method.toUpperCase()} ${endpoint}`,
      {
        params,
        ...(context ?? {}),
      },
      "API",
    );
  }

  static httpRequest(
    method: string,
    url: string,
    status: number,
    durationMs: number,
    context?: Record<string, unknown>,
  ) {
    this.recordLog(
      status >= 400 ? "ERROR" : "INFO",
      `[HTTP] ${method.toUpperCase()} ${url} -> ${status}`,
      {
        duration_ms: durationMs,
        ...(context ?? {}),
      },
      "HTTP",
    );
  }

  static userEvent(
    event: string,
    userId?: string,
    context?: Record<string, unknown>,
  ) {
    this.recordLog(
      "INFO",
      `[UserEvent:${event}]`,
      {
        user_id: userId,
        ...(context ?? {}),
      },
      "User",
    );
  }

  static navigationEvent(
    from: string,
    to: string,
    context?: Record<string, unknown>,
  ) {
    this.recordLog(
      "INFO",
      `[Navigation] ${from} -> ${to}`,
      context ?? {},
      "Navigation",
    );
  }

  static create(scope: string, context?: Record<string, unknown>) {
    return makeScopedLogger(scope, context);
  }

  static getLogs() {
    return [...Logger.logs];
  }

  static clearLogs() {
    Logger.logs = [];
  }
}

// Канонический логгер проекта: класс наружу не отдаём, чтобы не было двух имён одного значения.
export const logger = Logger;

// Фабрика логгеров с контекстом

function formatContext(ctx: LogContext): string {
  if (!ctx) return "";
  try {
    const json = JSON.stringify(ctx);
    return json === "{}" ? "" : ` ${json}`;
  } catch {
    return "";
  }
}

function makeScopedLogger(scope: string, initial?: LogContext) {
  const base = `[${scope}]` + formatContext(initial);
  const scoped = {
    log: (message: string, context?: Record<string, unknown>) =>
      Logger.log(`${base} ${message}`, context),
    info: (message: string, context?: Record<string, unknown>) =>
      Logger.info(`${base} ${message}`, context),
    warn: (message: string, context?: Record<string, unknown>) =>
      Logger.warn(`${base} ${message}`, context),
    warning: (message: string, context?: Record<string, unknown>) =>
      Logger.warning(`${base} ${message}`, context),
    error: (
      message: string,
      error?: Error,
      context?: Record<string, unknown>,
    ) => Logger.error(`${base} ${message}`, error, context),
    debug: (message: string, context?: Record<string, unknown>) =>
      Logger.debug(`${base} ${message}`, context),
    critical: (
      message: string,
      error?: Error,
      context?: Record<string, unknown>,
    ) => Logger.critical(`${base} ${message}`, error, context),
    child: (ctx?: LogContext) =>
      makeScopedLogger(scope, { ...(initial || {}), ...(ctx || {}) }),
    componentEvent: (
      component: string,
      event: string,
      context?: Record<string, unknown>,
    ) => Logger.componentEvent(`${scope}:${component}`, event, context),
    apiCall: (
      endpoint: string,
      method: string,
      params?: unknown,
      context?: Record<string, unknown>,
    ) => Logger.apiCall(`${scope}:${endpoint}`, method, params, context),
    httpRequest: (
      method: string,
      url: string,
      status: number,
      durationMs: number,
      context?: Record<string, unknown>,
    ) =>
      Logger.httpRequest(
        method,
        `${scope}:${url}`,
        status,
        durationMs,
        context,
      ),
    userEvent: (
      event: string,
      userId?: string,
      context?: Record<string, unknown>,
    ) => Logger.userEvent(`${scope}:${event}`, userId, context),
    navigationEvent: (
      from: string,
      to: string,
      context?: Record<string, unknown>,
    ) => Logger.navigationEvent(`${scope}:${from}`, `${scope}:${to}`, context),
  } as const;
  return scoped;
}

export const LoggerFactory = {
  forComponent(componentName: string, context?: LogContext) {
    return makeScopedLogger(`Component:${componentName}`, context);
  },
  forApiService(serviceName: string, context?: LogContext) {
    return makeScopedLogger(`API:${serviceName}`, context);
  },
} as const;
